class PressBar {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-press-track]');
    this.seq = root.querySelector('[data-press-seq]');

    if (!this.track || !this.seq) return;

    this.speed = parseFloat(getComputedStyle(root).getPropertyValue('--pb-speed')) || 50;
    this.pauseOnHover = root.dataset.pauseOnHover === 'true';

    this.offsetX = 0;
    this.seqWidth = 0;
    this.rafId = null;
    this.lastTime = null;

    this.loop = this.loop.bind(this);
    this.handlePointerEnter = this.handlePointerEnter.bind(this);
    this.handlePointerLeave = this.handlePointerLeave.bind(this);

    this.resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => this.handleResize()) : null;

    this.init();
  }

  imagesReady() {
    const imgs = Array.from(this.seq.querySelectorAll('img'));
    if (imgs.length === 0) return Promise.resolve();

    return Promise.all(
      imgs.map((img) => {
        if (img.complete && img.naturalWidth) return Promise.resolve();
        if (img.decode) return img.decode().catch(() => {});
        return new Promise((resolve) => {
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true });
        });
      })
    );
  }

  buildClones(containerWidth) {
    Array.from(this.track.querySelectorAll('.press-seq')).forEach((el, i) => {
      if (i > 0) el.remove();
    });

    this.seqWidth = this.seq.scrollWidth || 0;
    if (!this.seqWidth) return;

    const targetWidth = Math.max(containerWidth * 4, 3000);
    let totalWidth = this.seqWidth;

    while (totalWidth < targetWidth) {
      this.track.appendChild(this.seq.cloneNode(true));
      totalWidth += this.seqWidth;
    }
  }

  stop() {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.lastTime = null;
  }

  loop(ts) {
    if (this.lastTime == null) this.lastTime = ts;
    const dt = (ts - this.lastTime) / 1000;
    this.lastTime = ts;

    this.offsetX -= this.speed * dt;

    if (this.seqWidth && Math.abs(this.offsetX) >= this.seqWidth) {
      this.offsetX %= this.seqWidth;
    }

    this.track.style.transform = `translate3d(${this.offsetX.toFixed(3)}px, 0, 0)`;
    this.rafId = requestAnimationFrame(this.loop);
  }

  setup(preserveOffset) {
    this.stop();
    const container = this.track.parentElement;
    const containerWidth = container.clientWidth;
    const carry = preserveOffset && this.seqWidth ? this.offsetX % this.seqWidth || 0 : 0;

    this.buildClones(containerWidth);
    if (!this.seqWidth) return;

    this.offsetX = carry;
    this.lastTime = null;
    this.rafId = requestAnimationFrame(this.loop);
  }

  handleResize() {
    const container = this.track.parentElement;
    const width = container.clientWidth;
    if (this.lastInlineSize === undefined || Math.abs(width - this.lastInlineSize) >= 2) {
      this.lastInlineSize = width;
      this.setup(true);
    }
  }

  handlePointerEnter() {
    if (!this.pauseOnHover) return;
    if (this.rafId) cancelAnimationFrame(this.rafId);
  }

  handlePointerLeave() {
    if (!this.pauseOnHover) return;
    this.lastTime = null;
    this.rafId = requestAnimationFrame(this.loop);
  }

  init() {
    this.imagesReady()
      .then(() => (document.fonts && document.fonts.ready ? document.fonts.ready : null))
      .finally(() => {

        setTimeout(() => this.setup(false), 100);
      });

    const container = this.track.parentElement;
    this.lastInlineSize = container.clientWidth;

    if (this.resizeObserver) {
      this.resizeObserver.observe(container);
    } else {
      this._onWindowResize = () => this.handleResize();
      window.addEventListener('resize', this._onWindowResize, { passive: true });
    }

    if (this.pauseOnHover) {
      container.addEventListener('mouseenter', this.handlePointerEnter);
      container.addEventListener('mouseleave', this.handlePointerLeave);
    }
  }

  destroy() {
    this.stop();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this._onWindowResize) window.removeEventListener('resize', this._onWindowResize);
    const container = this.track && this.track.parentElement;
    if (container) {
      container.removeEventListener('mouseenter', this.handlePointerEnter);
      container.removeEventListener('mouseleave', this.handlePointerLeave);
    }
  }
}

const instances = new WeakMap();

function initPressBar(root) {
  if (instances.has(root)) return;
  instances.set(root, new PressBar(root));
}

function initAll(scope = document) {
  scope.querySelectorAll('[data-press-bar]').forEach(initPressBar);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll());
} else {
  initAll();
}

document.addEventListener('shopify:section:load', (event) => {
  initAll(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  const root = event.target.matches && event.target.matches('[data-press-bar]')
    ? event.target
    : event.target.querySelector && event.target.querySelector('[data-press-bar]');
  if (root && instances.has(root)) {
    instances.get(root).destroy();
    instances.delete(root);
  }
});